import React from 'react'
import { Button, Card } from '@/ui'

/**
 * Catches unhandled render errors and shows a user-friendly error screen.
 * Does not show stack traces to the user; logs to console only.
 */
interface ErrorBoundaryProps {
  children: React.ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  override componentDidCatch(error: Error): void {
    console.error('ErrorBoundary caught:', error)
  }

  private handleReload = (): void => {
    this.setState({ error: null })
    window.location.reload()
  }

  private handleGoToDashboard = (): void => {
    this.setState({ error: null })
    window.location.href = '/'
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-background p-4">
          <Card padded className="w-full max-w-md">
            <div className="space-y-4">
              <h1 className="text-headline-md text-on-surface">Something went wrong</h1>
              <p className="text-body-md text-on-surface-variant">
                {this.state.error.message || 'An unexpected error occurred.'}
              </p>
              <div className="flex gap-3 justify-center pt-2">
                <Button onClick={this.handleReload} variant="filled">
                  Reload
                </Button>
                <Button onClick={this.handleGoToDashboard} variant="text">
                  Go to dashboard
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )
    }

    return this.props.children
  }
}
